/**
 * ResizablePane Component
 *
 * A generic two-pane layout with drag-to-resize functionality.
 * Features:
 * - Drag divider to resize left pane
 * - Auto-collapse when dragged below threshold
 * - Collapsed rail with expand button
 * - Optional collapse button (overlay)
 * - localStorage persistence of width state
 * - Support for percentage-based maxWidth
 */

import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PanelLeft, ChevronsLeft } from 'lucide-react';

// Workspace packages
import { Button, Card } from '@enfomentis/ui';
import { Brand } from '@enfomentis/shared/config';

// =============================================================================
// Types
// =============================================================================

export interface ResizablePaneProps {
  /** localStorage key suffix for persisting width (prefixed with Brand.cachePrefix) */
  storageKey: string;
  /** Default width of left pane in pixels */
  defaultWidth: number;
  /** Minimum width of left pane in pixels (default: 200) */
  minWidth?: number;
  /** Maximum width - number for pixels, string for percentage (e.g., "50%") (default: 400) */
  maxWidth?: number | string;
  /** Width below which pane auto-collapses (default: 100) */
  collapseThreshold?: number;
  /** Width of collapsed rail in pixels (default: 28) */
  collapsedRailWidth?: number;
  /** Content for left pane */
  leftContent: ReactNode;
  /** Content for right pane */
  rightContent: ReactNode;
  /** Show collapse button on divider (default: true) */
  showCollapseButton?: boolean;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Build full localStorage key with brand prefix
 */
function buildStorageKey(suffix: string): string {
  return `${Brand.cachePrefix}-${suffix}`;
}

/**
 * Read number from localStorage with fallback
 */
function readStoredWidth(key: string, fallback: number): number {
  try {
    const stored = localStorage.getItem(key);
    if (stored !== null) {
      const parsed = JSON.parse(stored);
      if (typeof parsed === 'number' && !isNaN(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return fallback;
}

/**
 * Write number to localStorage
 */
function writeStoredWidth(key: string, value: number): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage errors (quota exceeded, etc.)
  }
}

/**
 * Parse maxWidth prop to pixel value
 */
function parseMaxWidth(
  maxWidth: number | string,
  containerWidth: number
): number {
  if (typeof maxWidth === 'number') {
    return maxWidth;
  }
  // Parse percentage string (e.g., "50%")
  const match = maxWidth.match(/^(\d+(?:\.\d+)?)%$/);
  if (match && match[1]) {
    const percent = parseFloat(match[1]);
    return Math.floor((containerWidth * percent) / 100);
  }
  // Fallback if invalid format
  return 400;
}

// =============================================================================
// Component
// =============================================================================

export const ResizablePane: React.FC<ResizablePaneProps> = ({
  storageKey,
  defaultWidth,
  minWidth = 200,
  maxWidth = 400,
  collapseThreshold = 100,
  collapsedRailWidth = 35,
  leftContent,
  rightContent,
  showCollapseButton = true,
}) => {
  // ---------------------------------------------------------------------------
  // Refs
  // ---------------------------------------------------------------------------

  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const dragStartWidthRef = useRef(0);

  // ---------------------------------------------------------------------------
  // Storage Keys
  // ---------------------------------------------------------------------------

  const widthKey = useMemo(() => buildStorageKey(storageKey), [storageKey]);
  const lastWidthKey = useMemo(
    () => buildStorageKey(`${storageKey}-last`),
    [storageKey]
  );

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [width, setWidth] = useState<number>(() =>
    readStoredWidth(widthKey, defaultWidth)
  );
  const [isDragging, setIsDragging] = useState(false);

  // Derived state
  const isCollapsed = width === 0;

  // ---------------------------------------------------------------------------
  // Mouse Event Handlers
  // ---------------------------------------------------------------------------

  const handleMouseMove = useCallback(
    (e: MouseEvent): void => {
      if (!isDraggingRef.current || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const maxWidthPx = parseMaxWidth(maxWidth, containerWidth);

      // Calculate new width based on mouse position relative to container
      const newWidth = e.clientX - containerRect.left;

      // Clamp width during drag:
      // - If in collapse zone (below threshold): allow 0 for collapse animation
      // - Otherwise: enforce minWidth to maxWidth for smooth behavior
      let clampedWidth: number;
      if (newWidth < collapseThreshold) {
        // In collapse zone - snap to 0
        clampedWidth = 0;
      } else {
        // Normal range - enforce min/max
        clampedWidth = Math.max(minWidth, Math.min(newWidth, maxWidthPx));
      }

      setWidth(clampedWidth);
    },
    [maxWidth, minWidth, collapseThreshold]
  );

  const handleMouseUp = useCallback((): void => {
    if (!isDraggingRef.current) return;

    isDraggingRef.current = false;
    setIsDragging(false);

    // Get final width and apply constraints
    setWidth((currentWidth) => {
      let finalWidth = currentWidth;

      // Handle snap-collapsed state (width already 0 from drag)
      // Need to persist lastWidth for expand to work correctly
      if (currentWidth === 0 && dragStartWidthRef.current > 0) {
        writeStoredWidth(lastWidthKey, dragStartWidthRef.current);
      }

      // Auto-collapse if below threshold (legacy path, kept for edge cases)
      if (currentWidth > 0 && currentWidth < collapseThreshold) {
        // Store current width before collapsing (use drag start width if available)
        const widthToStore =
          dragStartWidthRef.current > 0
            ? dragStartWidthRef.current
            : currentWidth;
        writeStoredWidth(lastWidthKey, widthToStore);
        finalWidth = 0;
      } else if (currentWidth > 0 && currentWidth < minWidth) {
        // Snap to minWidth if below (but above collapse threshold won't happen due to threshold < minWidth)
        // This handles the case when dragging from collapsed and releasing below minWidth
        finalWidth = minWidth;
      }

      // Persist final width
      writeStoredWidth(widthKey, finalWidth);
      return finalWidth;
    });

    // Cleanup listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [collapseThreshold, minWidth, widthKey, lastWidthKey, handleMouseMove]);

  const handleDividerMouseDown = useCallback(
    (e: React.MouseEvent): void => {
      e.preventDefault();
      isDraggingRef.current = true;
      dragStartWidthRef.current = width;
      setIsDragging(true);

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [width, handleMouseMove, handleMouseUp]
  );

  const handleRailMouseDown = useCallback(
    (e: React.MouseEvent): void => {
      // Prevent if clicking the expand button
      if ((e.target as HTMLElement).closest('button')) return;

      e.preventDefault();
      isDraggingRef.current = true;
      dragStartWidthRef.current = 0; // Starting from collapsed
      setIsDragging(true);

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [handleMouseMove, handleMouseUp]
  );

  // ---------------------------------------------------------------------------
  // Expand Handler
  // ---------------------------------------------------------------------------

  const handleExpand = useCallback((): void => {
    const restoredWidth = readStoredWidth(lastWidthKey, defaultWidth);
    // Ensure restored width is at least minWidth
    const finalWidth = Math.max(restoredWidth, minWidth);
    setWidth(finalWidth);
    writeStoredWidth(widthKey, finalWidth);
  }, [lastWidthKey, defaultWidth, minWidth, widthKey]);

  // ---------------------------------------------------------------------------
  // Collapse Handler
  // ---------------------------------------------------------------------------

  const handleCollapse = useCallback((): void => {
    // Guard: don't overwrite lastWidthKey if already collapsed
    if (width === 0) return;
    writeStoredWidth(lastWidthKey, width);
    setWidth(0);
    writeStoredWidth(widthKey, 0);
  }, [width, lastWidthKey, widthKey]);

  // ---------------------------------------------------------------------------
  // Cleanup on unmount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      ref={containerRef}
      className={`relative flex h-full w-full min-h-0 ${isDragging ? 'select-none' : ''}`}
    >
      {/* Collapsed Rail */}
      {isCollapsed && (
        <Card
          shadow="sm"
          radius="sm"
          className="flex flex-col items-center shrink-0 m-2 border-r-2 border-primary/50 bg-default-50 cursor-ew-resize"
          style={{ width: collapsedRailWidth }}
          onMouseDown={handleRailMouseDown}
        >
          <Button
            isIconOnly
            size="sm"
            variant="light"
            onPress={handleExpand}
            className=""
            aria-label="Expand panel"
          >
            <PanelLeft size={16} />
          </Button>
        </Card>
      )}

      {/* Left Pane */}
      {!isCollapsed && (
        <div
          className="shrink-0 min-h-0 overflow-hidden"
          style={{ width }}
        >
          {leftContent}
        </div>
      )}

      {/* Collapse Button - Overlay positioned at top-right edge of left pane */}
      {!isCollapsed && showCollapseButton && (
        <Button
          isIconOnly
          size="sm"
          radius="none"
          variant="bordered"
          onPress={handleCollapse}
          aria-label="Collapse panel"
          className="absolute z-10 border-none text-text-secondary "
          style={{ left: width - 28 }}
        >
          <ChevronsLeft size={16} />
        </Button>
      )}

      {/* Divider */}
      {!isCollapsed && (
        <div
          className={`shrink-0 w-1 cursor-col-resize transition-colors ${
            isDragging
              ? 'bg-primary/50'
              : 'bg-default-200 hover:bg-primary/50'
          }`}
          onMouseDown={handleDividerMouseDown}
        />
      )}

      {/* Right Pane */}
      <div className="flex-1 min-w-0 min-h-0 overflow-hidden">
        {rightContent}
      </div>
    </div>
  );
};
